/**
 * Visualizes a `CollisionZone`'s optional `fog` field as a low, drifting
 * ground-fog volume — "fog is a zone" (the field lives directly on
 * `CollisionZone`, not a separate top-level list), but rendering it is a
 * different concern from `debug-zones.ts`'s wireframe: that box is a DEBUG
 * aid (hidden unless DEBUG is toggled on, see index.ts's `debugZonesGroup`),
 * while fog has to be visible all the time, like a decal. So this builds a
 * second, independent mesh from the same zone — same split index.ts already
 * makes between the (debug-only) zone wireframe and the (always-visible)
 * decals.
 *
 * Three.js `THREE.Fog`/`FogExp2` is inherently scene-wide, not volumetric or
 * region-bound, so a real per-zone fog volume isn't available off the shelf.
 * This approximates one cheaply with the same "flat billboard card" idea
 * `grass-mesh.ts` uses for grass: a handful of horizontal, alpha-blended
 * quads stacked from the ground up to `fog.height`, each fainter than the
 * one below — reads as a soft volume from the game's fixed ~55° camera
 * pitch without any real raymarching. `fog.height` is display-only, the same
 * way `debug-zones.ts`'s `ZONE_DISPLAY_HEIGHT` is — `resolveBoxAt` in
 * base-sim.ts is XZ-only and never reads it.
 *
 * The alpha mask is a small procedurally-drawn cloud texture (a handful of
 * soft radial blobs, wrapped at the canvas edges so it tiles with no seam) —
 * no new asset dependency, in the same spirit as grass's hardcoded blade
 * layout. It's ONE shared texture for every fog zone in every map; drifting
 * it is just scrolling that one texture's UV offset each frame
 * (`updateFogDrift`), which animates every zone that references it at once
 * for effectively zero per-zone cost — cheaper even than grass's shared
 * uTime uniform, since there's no shader to compile at all.
 */
import * as THREE from 'three';
import type { CollisionZone } from '../types';

const FOG_LAYERS = 5;
// Bottom layer is closest to full opacity; each layer above fades further —
// this is what makes the stack read as "denser near the ground" rather than
// a uniform slab.
const FOG_LAYER_FALLOFF = 0.6;

const FOG_TEXTURE_SIZE = 128;
// Deterministic, not `Math.random()` — same reasoning as grass-mesh.ts's
// `BLADE_OFFSETS`: every fog zone's cloud pattern should look identical on
// every load, not vary by draw order.
const FOG_BLOBS: ReadonlyArray<{ x: number; y: number; r: number; a: number }> = [
  { x: 0.18, y: 0.28, r: 0.26, a: 0.9 },
  { x: 0.62, y: 0.18, r: 0.22, a: 0.7 },
  { x: 0.42, y: 0.58, r: 0.3, a: 0.85 },
  { x: 0.82, y: 0.68, r: 0.24, a: 0.65 },
  { x: 0.08, y: 0.78, r: 0.2, a: 0.6 },
  { x: 0.55, y: 0.9, r: 0.18, a: 0.55 },
];

function buildFogAlphaTexture(): THREE.Texture {
  const size = FOG_TEXTURE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.Texture(); // headless/test environment — never sampled there
  ctx.clearRect(0, 0, size, size);
  for (const b of FOG_BLOBS) {
    const cx = b.x * size;
    const cy = b.y * size;
    const r = b.r * size;
    // Each blob is also drawn shifted by ±size in x/y — the 8 wrapped copies
    // of any blob that straddles an edge, so RepeatWrapping tiles it with no
    // visible seam.
    for (const dx of [-size, 0, size]) {
      for (const dy of [-size, 0, size]) {
        const grad = ctx.createRadialGradient(cx + dx, cy + dy, 0, cx + dx, cy + dy, r);
        grad.addColorStop(0, `rgba(255,255,255,${b.a})`);
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx + dx, cy + dy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// Built once, shared forever, mutated (never replaced) by `updateFogDrift` —
// same "module-scoped, never disposed" reasoning as scene.ts's fallback
// ground texture and grass-mesh.ts's shared geometry/material.
const FOG_ALPHA_TEXTURE = buildFogAlphaTexture();

// Unit-sized, sized per zone via the group's `.scale` — same technique
// debug-zones.ts's `buildZoneMesh` and decal-mesh.ts's `buildDecalMesh` use,
// for the same reason (TransformControls' scale mode drives `.scale`
// directly with no per-drag-frame geometry rebuild).
const FOG_LAYER_GEOMETRY = new THREE.PlaneGeometry(1, 1);

/**
 * `null` when the zone has no `fog` — callers skip adding anything to the
 * scene in that case, same as any other optional-field-gated visual.
 */
export function buildFogZoneMesh(zone: CollisionZone): THREE.Object3D | null {
  if (!zone.fog) return null;
  const { color, opacity, height } = zone.fog;
  const group = new THREE.Group();
  for (let i = 0; i < FOG_LAYERS; i += 1) {
    const t = FOG_LAYERS > 1 ? i / (FOG_LAYERS - 1) : 0;
    // Each layer gets its own material (not shared across layers/zones like
    // grass's) because color/opacity/height are per-zone author choices, not
    // a shared constant — cloning is cheap at "a handful of zones, a
    // handful of layers each", nowhere near grass's "spawned a lot" scale.
    const material = new THREE.MeshBasicMaterial({
      map: FOG_ALPHA_TEXTURE,
      color,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      opacity: opacity * (1 - t * FOG_LAYER_FALLOFF),
    });
    const layer = new THREE.Mesh(FOG_LAYER_GEOMETRY, material);
    layer.rotation.x = -Math.PI / 2; // lie flat, facing +Y — same as decal-mesh.ts's plane
    layer.position.y = t * height;
    group.add(layer);
  }
  // Same T*R*S composition note as decal-mesh.ts: scale first sizes the
  // (already-flattened) stack along its own unrotated axes, rotationY then
  // spins the sized stack rigidly, so an angled zone's fog doesn't shear.
  group.scale.set(zone.size.w, 1, zone.size.d);
  group.position.set(zone.position.x, 0, zone.position.z);
  group.rotation.y = zone.rotationY ?? 0;
  return group;
}

/**
 * Scrolls the one shared alpha texture's UV offset — every fog zone
 * referencing it drifts together, at a cost independent of how many zones
 * exist. Call once per frame from each frame loop that also calls
 * `SceneHandles.updateGrass` (index.ts / location-view.ts /
 * ultra_map_editor's own loop).
 */
export function updateFogDrift(dt: number): void {
  FOG_ALPHA_TEXTURE.offset.x = (FOG_ALPHA_TEXTURE.offset.x + dt * 0.015) % 1;
  FOG_ALPHA_TEXTURE.offset.y = (FOG_ALPHA_TEXTURE.offset.y + dt * 0.008) % 1;
}
